import type { ThemeOptions } from '@mui/material';

import type { Theme } from '../../themes/themes';

export const createTypographyTheme = (theme: Theme): ThemeOptions => ({
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        h3: { display: 'block' }, // Temporary fix for h3, skp_rechner_ng.css sets h3 to display: none
        cite: {
          display: 'block',
        },
        'cite::before': {
          content: '"„"',
        },
        'cite::after': {
          content: '"“"',
        },
        a: {
          color: 'inherit',
          textDecoration: 'none',
        },
        '[role=link]': {
          cursor: 'pointer',
        },
      },
    },
  },

  typography: {
    ...theme.typography,

    h1: {
      fontFamily: 'var(--font-sparkasse-head, Sparkasse Head, Sparkasse Regular, Sparkasse Web)',
      fontSize: 24,
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: 0,
      [theme.breakpoints.up('lg')]: {
        fontSize: 60,
        lineHeight: 1.08,
      },
    },
    h2: {
      fontFamily: 'var(--font-sparkasse-head,Sparkasse Head,Sparkasse Regular,Sparkasse Web,sans-serif)',
      fontSize: 20,
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: 0,
      [theme.breakpoints.up('lg')]: {
        fontSize: 42,
        lineHeight: 1.14,
      },
      'body.if6 &': {
        font: '700 20px/130% SparkasseWeb,Helvetica,Arial,sans-serif',
        fontFamily: 'var(--font-sparkasse-head,SparkasseWeb,Helvetica,Arial,sans-serif)',
        fontSize: 20,
        fontWeight: 700,
        lineHeight: 1.3,
        letterSpacing: 0,
        [theme.breakpoints.up('lg')]: {
          fontSize: 42,
          lineHeight: 1.14,
        },
      },
    },
    h3: {
      fontFamily: 'var(--font-sparkasse-head, Sparkasse Head, Sparkasse Regular, Sparkasse Web)',
      fontSize: 18,
      fontWeight: 700,
      lineHeight: 1.44,
      letterSpacing: 0,
      [theme.breakpoints.up('lg')]: {
        fontSize: 30,
        lineHeight: 1.4,
      },
      'body.if6 &': {
        font: '700 18px/144% SparkasseWeb,Helvetica,Arial,sans-serif',
        fontFamily: 'var(--font-sparkasse-head,SparkasseWeb,Helvetica,Arial,sans-serif)',
        fontSize: 18,
        fontWeight: 700,
        lineHeight: 1.44,
        letterSpacing: 0,
        [theme.breakpoints.up('lg')]: {
          fontSize: 30,
          lineHeight: 1.4,
        },
      },
    },
    h4: {
      fontFamily: 'var(--font-sparkasse-medium, SparkasseWebMedium, Sparkasse Medium, Sparkasse Web)',
      fontSize: 16,
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: '-0.022em',
      [theme.breakpoints.up('lg')]: {
        fontSize: 24,
        lineHeight: 1.33,
        letterSpacing: '-0.025em',
      },
      'body.if6 &': {
        font: '500 16px/150% SparkasseWeb,Helvetica,Arial,sans-serif',
        fontFamily: 'var(--font-sparkasse-medium, SparkasseWebMedium,Helvetica,Arial,sans-serif)',
        fontSize: 16,
        fontWeight: 500,
        lineHeight: 1.5,
        letterSpacing: '-0.022em',
        [theme.breakpoints.up('lg')]: {
          fontSize: 24,
          lineHeight: 1.33,
          letterSpacing: '-0.025em',
        },
      },
    },
    h5: {
      fontFamily: 'var(--font-sparkasse-medium, SparkasseWebMedium, Sparkasse Medium, Sparkasse Web)',
      fontSize: 14,
      fontWeight: 500,
      lineHeight: 1.29,
      letterSpacing: '-0.02em',
      [theme.breakpoints.up('lg')]: {
        fontSize: 20,
        lineHeight: 1.3,
        letterSpacing: '-0.025em',
      },
    },
    h6: {
      fontFamily: 'var(--font-sparkasse-regular,SparkasseWeb,Sparkasse Regular,Sparkasse Web,sans-serif)',
      fontSize: 13,
      fontWeight: 400,
      lineHeight: 1.38,
      letterSpacing: '-0.02em',
      [theme.breakpoints.up('lg')]: {
        fontSize: 16,
        lineHeight: 1.37,
        letterSpacing: '-0.018em',
      },
      'body.if6 &': {
        font: '400 13px/138% SparkasseWeb,Helvetica,Arial,sans-serif',
        fontFamily: 'var(--font-sparkasse-regular,SparkasseWeb,Helvetica,Arial,sans-serif)',
        fontSize: 13,
        fontWeight: 400,
        lineHeight: 1.38,
        letterSpacing: '-0.02em',
        [theme.breakpoints.up('lg')]: {
          fontSize: 16,
          lineHeight: 1.37,
          letterSpacing: '-0.018em',
        },
      },
    },
    overline: {
      fontFamily: 'var(--font-sparkasse-regular, SparkasseWeb, Sparkasse Regular, Sparkasse Web)',
      fontSize: 13,
      fontWeight: 400,
      lineHeight: 1.38,
      letterSpacing: '-0.02em',
      textTransform: 'none',
      [theme.breakpoints.up('lg')]: {
        fontSize: 16,
        lineHeight: 1.37,
        letterSpacing: '-0.018em',
      },
    },
    button: {
      //b1
      fontFamily: 'var(--font-sparkasse-b1, SparkasseWeb, Sparkasse B1, Sparkasse Web)',
      fontSize: 13,
      fontWeight: 400,
      lineHeight: 1.385,
      letterSpacing: '-0.02em',
      textTransform: 'none',
      [theme.breakpoints.up('lg')]: {
        fontSize: 16,
        lineHeight: 1.62,
        letterSpacing: '-0.015em',
      },
    },
    body1: {
      //p1
      fontFamily: 'var(--font-sparkasse-p1, SparkasseWebLight, Sparkasse P1, Sparkasse Web)',
      fontSize: 16,
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '-0.02em',
      [theme.breakpoints.up('lg')]: {
        fontSize: 24,
      },
    },
    body2: {
      // == p2
      fontFamily: 'var(--font-sparkasse-light, SparkasseWebLight, Sparkasse Light, Sparkasse Web)',
      fontSize: 13,
      fontWeight: 400,
      lineHeight: 1.44,
      letterSpacing: '-0.025em',
      [theme.breakpoints.up('lg')]: {
        fontSize: 18,
        letterSpacing: '-0.02em',
      },
      'body.if6 &': {
        font: '400 13px/144% SparkasseWeb,Helvetica,Arial,sans-serif',
        fontFamily: 'var(--font-sparkasse-light,SparkasseWebLight,Helvetica,Arial,sans-serif)',
        fontSize: 13,
        fontWeight: 400,
        lineHeight: 1.44,
        letterSpacing: '-0.025em',
        [theme.breakpoints.up('lg')]: {
          fontSize: 18,
          letterSpacing: '-0.02em',
        },
      },
    },
    c1: {
      fontFamily: 'var(--font-sparkasse-serif, SparkasseWeb, Sparkasse Serif, Sparkasse Web)',
      fontSize: 24,
      fontWeight: 400,
      lineHeight: 1.29,
      letterSpacing: '-0.015em',
      [theme.breakpoints.up('lg')]: {
        fontSize: 42,
        lineHeight: 1.43,
        letterSpacing: '-0.02em',
      },
    },
    c2: {
      fontFamily: 'var(--font-sparkasse-serif, SparkasseWeb, Sparkasse Serif, Sparkasse Web)',
      fontSize: 18,
      fontWeight: 400,
      lineHeight: 1.33,
      letterSpacing: '-0.018em',
      [theme.breakpoints.up('lg')]: {
        fontSize: 30,
        lineHeight: 1.4,
        letterSpacing: '-0.032em',
      },
    },
    c3: {
      fontFamily: 'var(--font-sparkasse-serif, SparkasseWeb, Sparkasse Serif, Sparkasse Web)',
      fontSize: 15,
      fontWeight: 400,
      lineHeight: 1.33,
      letterSpacing: '-0.02em',
      [theme.breakpoints.up('lg')]: {
        fontSize: 20,
        lineHeight: 1.4,
        letterSpacing: '-0.018em',
      },
    },
    responsive1: {
      fontFamily: 'var(--font-sparkasse-medium, SparkasseWeb, Sparkasse Medium, Sparkasse Web)',
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1.38,
      letterSpacing: '-0.02em',
      textTransform: 'none',
      [theme.breakpoints.up('lg')]: {
        fontSize: 20,
        fontWeight: 500,
        lineHeight: 1.3,
        letterSpacing: '-0.025em',
      },
    },
    responsive2: {
      fontFamily: 'var(--font-sparkasse-regular, SparkasseWeb, Sparkasse Regular, Sparkasse Web)',
      fontSize: 13,
      fontWeight: 400,
      lineHeight: 1.38,
      letterSpacing: '-0.02em',
      [theme.breakpoints.up('lg')]: {
        fontSize: 24,
        lineHeight: 1.33,
        letterSpacing: '-0.025em',
      },
    },
    openerFullPage: {
      fontFamily: 'var(--font-sparkasse-head, Sparkasse Head, Sparkasse Regular, Sparkasse Web)',
      fontSize: 24,
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '-0.03em',
      [theme.breakpoints.up('md')]: {
        fontSize: 42,
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: 60,
        lineHeight: 1.08,
        letterSpacing: '-0.02em',
      },
    },
    referenceNumber: {
      fontFamily: 'var(--font-sparkasse-medium, SparkasseWebMedium, Sparkasse Medium, Sparkasse Web)',
      fontSize: 30,
      fontWeight: 400,
      lineHeight: 1.4,
      letterSpacing: '-0.025em',
      [theme.breakpoints.up('lg')]: {
        fontSize: 42,
        lineHeight: 1.14,
        letterSpacing: '-0.02em',
      },
    },
  },
});
